import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ConversationMessage } from '../services/assistantChatService';

export type ViewMode = 'workflow' | 'chat';

interface AssistantChatState {
  // Messages by session key (workflowId-runInstanceId-stepId)
  messagesBySession: Record<string, ConversationMessage[]>;

  // Current active session
  activeSessionKey: string | null;

  // View mode preference
  viewMode: ViewMode;

  // UI state
  isTyping: boolean;
  isLoading: boolean;
  error: string | null;

  // Conversation metadata
  sessionMetadata: Record<string, {
    workflowId: number;
    runInstanceId: number;
    stepId: number;
    sessionMode?: 'isolated' | 'shared' | 'run';
    sessionId?: string;
    startedAt: string;
    lastMessageAt?: string;
    turnCount: number;
    maxTurns?: number;
  }>;

  // Actions
  setMessages: (messages: ConversationMessage[], sessionKey?: string) => void;
  addMessage: (message: ConversationMessage, sessionKey?: string) => void;
  updateMessage: (messageId: string, updates: Partial<ConversationMessage>, sessionKey?: string) => void;
  deleteMessage: (messageId: string, sessionKey?: string) => void;
  clearMessages: (sessionKey?: string) => Promise<void>;
  getMessages: () => ConversationMessage[];

  setActiveSession: (sessionKey: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setIsTyping: (isTyping: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Session management
  createSession: (workflowId: number, runInstanceId: number, stepId: number, sessionMode?: 'isolated' | 'shared' | 'run') => string;
  deleteSession: (sessionKey: string) => void;
  updateSessionMetadata: (sessionKey: string, updates: Partial<AssistantChatState['sessionMetadata'][string]>) => void;

  // Computed getters
  currentSessionMetadata: AssistantChatState['sessionMetadata'][string] | null;
  hasAssistantSteps: (workflowId: number) => boolean;
}

const createSessionKey = (workflowId: number, runInstanceId: number, stepId: number, sessionMode?: 'isolated' | 'shared' | 'run'): string => {
  // Match backend session key format from assistant_session_manager.py
  if (sessionMode === 'shared') {
    return `assistant_${workflowId}`;
  }
  // For isolated sessions, include runInstanceId to ensure each run is fresh
  if (sessionMode === 'isolated') {
    return `assistant_${stepId}_${runInstanceId}`;
  }
  // For run-scoped sessions (default), include runInstanceId
  return `assistant_${workflowId}_${runInstanceId}`;
};

export const useAssistantChatStore = create<AssistantChatState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        messagesBySession: {},
        activeSessionKey: null,
        viewMode: 'workflow',
        isTyping: false,
        isLoading: false,
        error: null,
        sessionMetadata: {},

        // Message management
        setMessages: (messages, sessionKey) => {
          const key = sessionKey || get().activeSessionKey;
          console.log('[DEBUG] AssistantChatStore - setMessages called');
          console.log('[DEBUG] AssistantChatStore - messages:', JSON.stringify(messages, null, 2));
          console.log('[DEBUG] AssistantChatStore - sessionKey param:', sessionKey);
          console.log('[DEBUG] AssistantChatStore - activeSessionKey:', get().activeSessionKey);
          console.log('[DEBUG] AssistantChatStore - key:', key);
          
          if (!key) {
            console.log('[DEBUG] AssistantChatStore - No key, returning early');
            return;
          }

          console.log('[DEBUG] AssistantChatStore - Setting messages for key:', key);
          set((state) => ({
            messagesBySession: {
              ...state.messagesBySession,
              [key]: messages
            }
          }));

          // Update session metadata
          const metadata = get().sessionMetadata[key];
          if (metadata && messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            set((state) => ({
              sessionMetadata: {
                ...state.sessionMetadata,
                [key]: {
                  ...metadata,
                  lastMessageAt: lastMessage.timestamp || new Date().toISOString(),
                  turnCount: messages.filter(m => m.role === 'user').length
                }
              }
            }));
          }
          console.log('[DEBUG] AssistantChatStore - Messages set successfully');
        },

        addMessage: (message, sessionKey) => {
          const key = sessionKey || get().activeSessionKey;
          if (!key) return;

          set((state) => ({
            messagesBySession: {
              ...state.messagesBySession,
              [key]: [...(state.messagesBySession[key] || []), message]
            }
          }));

          // Update session metadata
          const metadata = get().sessionMetadata[key];
          if (metadata) {
            set((state) => ({
              sessionMetadata: {
                ...state.sessionMetadata,
                [key]: {
                  ...metadata,
                  lastMessageAt: message.timestamp || new Date().toISOString(),
                  turnCount: message.role === 'user'
                    ? metadata.turnCount + 1
                    : metadata.turnCount
                }
              }
            }));
          }
        },

        updateMessage: (messageId, updates, sessionKey) => {
          const key = sessionKey || get().activeSessionKey;
          if (!key) return;

          set((state) => ({
            messagesBySession: {
              ...state.messagesBySession,
              [key]: (state.messagesBySession[key] || []).map((msg) =>
                msg.id === messageId ? { ...msg, ...updates } : msg
              )
            }
          }));
        },

        deleteMessage: (messageId, sessionKey) => {
          const key = sessionKey || get().activeSessionKey;
          if (!key) return;

          set((state) => ({
            messagesBySession: {
              ...state.messagesBySession,
              [key]: (state.messagesBySession[key] || []).filter(
                (msg) => msg.id !== messageId
              )
            }
          }));
        },

        clearMessages: async (sessionKey) => {
          const key = sessionKey || get().activeSessionKey;
          if (!key) return;

          try {
            set({ isLoading: true });

            // Call the backend API to clear session
            const apiService = (await import('../services/api')).default;
            const response = await apiService.clearSession(key, true);

            console.log(`[DEBUG] AssistantChatStore - clearMessages: Cleared ${response.messages_deleted} messages`);

            // Update local state after successful deletion
            set((state) => ({
              messagesBySession: {
                ...state.messagesBySession,
                [key]: []
              }
            }));

            // Reset turn count in metadata
            const metadata = get().sessionMetadata[key];
            if (metadata) {
              set((state) => ({
                sessionMetadata: {
                  ...state.sessionMetadata,
                  [key]: {
                    ...metadata,
                    turnCount: 0,
                    lastMessageAt: undefined
                  }
                }
              }));
            }

            // CRITICAL FIX: Explicitly update localStorage to ensure persist middleware syncs
            // This is necessary because Zustand's persist middleware may not immediately sync
            // state changes, causing cleared messages to reappear after page reload
            try {
              const storageKey = 'assistant-chat-storage';
              const currentStorage = localStorage.getItem(storageKey);
              if (currentStorage) {
                const parsedStorage = JSON.parse(currentStorage);
                const stateToSave = parsedStorage.state || {};

                // Update the persisted state with cleared messages
                if (stateToSave.messagesBySession && stateToSave.messagesBySession[key]) {
                  stateToSave.messagesBySession[key] = [];
                }
                if (stateToSave.sessionMetadata && stateToSave.sessionMetadata[key]) {
                  stateToSave.sessionMetadata[key] = {
                    ...stateToSave.sessionMetadata[key],
                    turnCount: 0,
                    lastMessageAt: undefined
                  };
                }

                // Save back to localStorage
                parsedStorage.state = stateToSave;
                localStorage.setItem(storageKey, JSON.stringify(parsedStorage));
                console.log('[DEBUG] AssistantChatStore - clearMessages: Explicitly updated localStorage to sync cleared state');
              }
            } catch (storageError: any) {
              console.warn('[DEBUG] AssistantChatStore - clearMessages: Failed to explicitly update localStorage:', storageError);
            }

            set({ isLoading: false });

            // Dispatch custom event to notify ChatInterface that session was cleared
            // This prevents auto-reload from restoring messages from previous runs
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('sessionCleared', { detail: { sessionKey: key } }));
              console.log('[DEBUG] AssistantChatStore - clearMessages: Dispatched sessionCleared event for key:', key);
            }

          } catch (error: any) {
            console.error('[DEBUG] AssistantChatStore - clearMessages: Failed to clear session:', error);
            set({
              isLoading: false,
              error: error.message || 'Failed to clear conversation'
            });
            // Still clear local state even if API fails
            set((state) => ({
              messagesBySession: {
                ...state.messagesBySession,
                [key]: []
              }
            }));
          }
        },

        // Session management
        setActiveSession: (sessionKey) => {
          set({ activeSessionKey: sessionKey });
        },

        createSession: (workflowId, runInstanceId, stepId, sessionMode) => {
          const sessionKey = createSessionKey(workflowId, runInstanceId, stepId, sessionMode);

          set((state) => ({
            messagesBySession: {
              ...state.messagesBySession,
              [sessionKey]: state.messagesBySession[sessionKey] || []
            },
            sessionMetadata: {
              ...state.sessionMetadata,
              [sessionKey]: state.sessionMetadata[sessionKey] || {
                workflowId,
                runInstanceId,
                stepId,
                sessionMode,
                startedAt: new Date().toISOString(),
                turnCount: 0
              }
            },
            activeSessionKey: sessionKey
          }));

          return sessionKey;
        },

        deleteSession: (sessionKey) => {
          set((state) => {
            const { [sessionKey]: deletedMessages, ...remainingMessages } = state.messagesBySession;
            const { [sessionKey]: deletedMetadata, ...remainingMetadata } = state.sessionMetadata;

            return {
              messagesBySession: remainingMessages,
              sessionMetadata: remainingMetadata,
              activeSessionKey: state.activeSessionKey === sessionKey ? null : state.activeSessionKey
            };
          });
        },

        updateSessionMetadata: (sessionKey, updates) => {
          const metadata = get().sessionMetadata[sessionKey];
          if (!metadata) return;

          set((state) => ({
            sessionMetadata: {
              ...state.sessionMetadata,
              [sessionKey]: {
                ...metadata,
                ...updates
              }
            }
          }));
        },

        // UI state
        setViewMode: (mode) => set({ viewMode: mode }),
        setIsTyping: (isTyping) => set({ isTyping }),
        setIsLoading: (isLoading) => set({ isLoading }),
        setError: (error) => set({ error }),

        // Get messages for the active session
        getMessages: () => {
          const key = get().activeSessionKey;
          console.log('[DEBUG] AssistantChatStore - getMessages called');
          console.log('[DEBUG] AssistantChatStore - activeSessionKey:', key);
          console.log('[DEBUG] AssistantChatStore - messagesBySession:', get().messagesBySession);
          if (!key) {
            console.log('[DEBUG] AssistantChatStore - No activeSessionKey, returning empty array');
            return [];
          }
          const messages = get().messagesBySession[key] || [];
          console.log('[DEBUG] AssistantChatStore - Returning messages for key', key, ':', JSON.stringify(messages, null, 2));
          return messages;
        },

        get currentSessionMetadata() {
          const key = get().activeSessionKey;
          if (!key) return null;
          return get().sessionMetadata[key] || null;
        },

        hasAssistantSteps: (workflowId) => {
          // Check if any session metadata exists for this workflow with assistant steps
          return Object.values(get().sessionMetadata).some(
            (meta) => meta.workflowId === workflowId
          );
        }
      }),
      {
        name: 'assistant-chat-storage',
        // Only persist certain fields
        partialize: (state) => ({
          messagesBySession: state.messagesBySession,
          sessionMetadata: state.sessionMetadata,
          viewMode: state.viewMode
        })
      }
    )
  )
);

// Selector hooks for common use cases
export const useActiveMessages = () => {
  return useAssistantChatStore((state) => {
    const key = state.activeSessionKey;
    if (!key) return [];
    return state.messagesBySession[key] || [];
  });
};

export const useSessionMetadata = (sessionKey?: string) => {
  return useAssistantChatStore((state) => {
    const key = sessionKey || state.activeSessionKey;
    if (!key) return null;
    return state.sessionMetadata[key] || null;
  });
};

export const useIsConversationActive = () => {
  return useAssistantChatStore((state) => {
    const metadata = state.currentSessionMetadata;
    if (!metadata) return false;

    // Check if we're below max turns
    if (metadata.maxTurns && metadata.turnCount >= metadata.maxTurns) {
      return false;
    }

    // Check if last message was recent (within 30 minutes)
    if (metadata.lastMessageAt) {
      const lastMessageTime = new Date(metadata.lastMessageAt).getTime();
      const now = Date.now();
      const thirtyMinutes = 30 * 60 * 1000;
      return (now - lastMessageTime) < thirtyMinutes;
    }

    return true;
  });
};